import { Any, Timestamp } from '@bufbuild/protobuf';
import { toFixed } from '@cosmjs/math';
import { decodePubkey } from '@cosmjs/proto-signing';
import { generateTypes } from '@evmos/eip712';
import { createAnyMessage } from '@evmos/proto/dist/messages/common';
import { TxContext, TxPayload, createTransactionPayload } from '@evmos/transactions';
import { EIP712TypedData } from '@evmos/transactions/dist/messages/base';
import { MsgCreateValidator } from 'cosmjs-types/cosmos/staking/v1beta1/tx';
import { MsgCreateValidator as MsgCreateValidatorProto } from '../proto/cosmos/staking/v1beta1/tx_pb';

type CreateValidatorParams = Required<MsgCreateValidator>;

const MSG_CREATE_VALIDATOR_TYPES = (params: CreateValidatorParams) => ({
  MsgValue: [
    { name: 'description', type: 'TypeDescription' },
    { name: 'commission', type: 'TypeCommission' },
    { name: 'min_self_delegation', type: 'string' },
    { name: 'delegator_address', type: 'string' },
    { name: 'validator_address', type: 'string' },
    { name: 'pubkey', type: 'TypePubkey' },
    { name: 'value', type: 'TypeValue' },
  ],
  TypeDescription: [
    { name: 'moniker', type: 'string' },
    ...(params.description.identity ? [ { name: 'identity', type: 'string' } ] : []),
    ...(params.description.website ? [ { name: 'website', type: 'string' } ] : []),
    ...(params.description.securityContact ? [ { name: 'security_contact', type: 'string' } ] : []),
    ...(params.description.details ? [ { name: 'details', type: 'string' } ] : []),
  ],
  TypeCommission: [
    { name: 'rate', type: 'string' },
    { name: 'max_rate', type: 'string' },
    { name: 'max_change_rate', type: 'string' },
  ],
  TypePubkey: [
    { name: 'type', type: 'string' },
    { name: 'value', type: 'string' },
  ],
  TypeValue: [
    { name: 'denom', type: 'string' },
    { name: 'amount', type: 'string' },
  ],
});

export const createTxMsgCreateValidator = (context: TxContext, params: CreateValidatorParams): TxPayload => {
  const typedData = createEIP712MsgCreateValidator(context, params);
  const cosmosMsg = createCosmosMsgCreateValidator(context, params);
  return createTransactionPayload(context, typedData, cosmosMsg);
};

const createEIP712MsgCreateValidator = (context: TxContext, params: CreateValidatorParams): EIP712TypedData => {
  const types = generateTypes(MSG_CREATE_VALIDATOR_TYPES(params));
  const message = createMsgCreateValidator(context, params);
  return { types, message };
};

const createCosmosMsgCreateValidator = (context: TxContext, params: CreateValidatorParams): any => {
  const message = new MsgCreateValidatorProto(params);
  return { message, path: MsgCreateValidatorProto.typeName };
};

function createMsgCreateValidator(context: TxContext, params: CreateValidatorParams): object {
  return {
    type: 'cosmos-sdk/MsgCreateValidator',
    value: {
      description: {
        moniker: params.description.moniker,
        ...(params.description.identity && { identity: params.description.identity }),
        ...(params.description.website && { website: params.description.website }),
        ...(params.description.securityContact && { security_contact: params.description.securityContact }),
        ...(params.description.details && { details: params.description.details }),
      },
      commission: {
        rate: toFixed(Number(params.commission.rate) / Math.pow(10, 18)),
        max_rate: toFixed(Number(params.commission.maxRate) / Math.pow(10, 18)),
        max_change_rate: toFixed(Number(params.commission.maxChangeRate) / Math.pow(10, 18)),
      },
      min_self_delegation: params.minSelfDelegation,
      delegator_address: params.delegatorAddress,
      validator_address: params.validatorAddress,
      pubkey: decodePubkey(params.pubkey),
      value: params.value,
    },
  };
}

